import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ImageProject,
    ImageState,
    TextProps,
    InlineText,
    ImageHistoryEntry,
} from '../../shared/types/imagesType';

import { ImageAttributes } from '../../shared/types/imagesType';
import {
    updateImage,
    updateImageUrl,
    uploadImage,
    fetchUserUploads,
    downloadImage,
    fetchImageProject,
    updateImageProject,
    generateImage,
    enhanceImageUrl,
    resetImageUrl,
    wizardImageUrl,
    removeBackgroundImageUrl,
    updatePosition,
    updateZoom,
    updateTransformation,
} from '../../shared/api/imageProjectApi';
import {
    createImageLayout,
    deleteImageLayout,
    updateImageLayout,
    addImagesToLayout,
    changeImageOrder,
    deleteImageFromLayout,
} from '../../shared/api/imageLayoutApi';
import { ImageLayoutAttributes } from '../../shared/types/imageLayoutTypes';
import { getSlotCountBySlug } from '../../shared/helpers/transformData';
import { normalizeCanvasColorGradient } from '../../shared/helpers/imageHelpers';
import { AppDispatch, RootState } from '../store';

export const initialState: ImageState = {
    imageProject: null,
    selectedImage: { image: null, layoutId: null },
    selectedSlotIndex: { slotIndex: null, layoutId: null }, //for empty slot
    // temporaryLayout: null,
    imageLayout: null,
    temperoryTexts: [],
    selectedText: null,
    uploadImages: [],
    error: null,
    downloadStatus: {
        loading: false,
        error: null,
    },
    loading: false,
    loadingSave: false,
    loadingCancel: false,
    loadingCreateLayout: false,
    loadingDownload: false,
    draggedImage: null,
    hasChanged: false,
    history: [],
    future: [],
};

const HISTORY_LIMIT = 200;

function deepClone<T>(v: T): T {
    try {
        return structuredClone(v);
    } catch {
        return JSON.parse(JSON.stringify(v));
    }
}

function makeSnapshot(state: ImageState) {
    return {
        selectedImage: deepClone(state.selectedImage),
        imageProject: deepClone(state.imageProject),
        imageLayout: deepClone(state.imageLayout),
        temperoryTexts: deepClone(state.temperoryTexts),
    };
}

function cappedUnshift<T>(arr: T[], item: T, limit = HISTORY_LIMIT) {
    arr.push(item);
    if (arr.length > limit) arr.splice(0, arr.length - limit);
}

const imageSlice = createSlice({
    name: 'image',
    initialState,
    reducers: {
        commitHistory: (
            state,
            action: PayloadAction<{
                label: string;
                opts?: { groupId?: string; replace?: boolean };
            }>,
        ) => {
            const { label, opts } = action.payload;
            safePushHistory(state, label, opts);
        },
        logHistory: (state, action: PayloadAction<ImageHistoryEntry>) => {
            state.history.push(action.payload);
            state.future = [];
        },
        undoHistory: (state) => {
            const idx = state.history.length - 1;
            if (idx <= 0) return;

            const current = state.history[idx];
            const previous = state.history[idx - 1];

            state.selectedImage = previous.value.selectedImage ?? { image: null, layoutId: null };
            state.imageProject = previous.value.imageProject ?? null;
            state.imageLayout = previous.value.imageLayout ?? null;
            state.temperoryTexts = previous.value.temperoryTexts ?? [];

            state.future.unshift(current);
            state.history.pop();
        },

        clearHistory: (state) => {
            state.history = [];
            state.future = [];
        },
        setLoadingDownload: (state, action: PayloadAction<boolean>) => {
            state.loadingDownload = action.payload;
        },
        setLoadingSave: (state, action: PayloadAction<boolean>) => {
            state.loadingSave = action.payload;
        },
        setLoadingCancel: (state, action: PayloadAction<boolean>) => {
            state.loadingCancel = action.payload;
        },

        setAllImagesForLayouts: (state, action: PayloadAction<ImageAttributes>) => {
            if (state.imageProject) {
                state.imageProject.allImagesForLayouts = [
                    ...state.imageProject.allImagesForLayouts,
                    action.payload,
                ];
                // state.hasChanged = true;
            }
        },
        setImageLayout: (state, action: PayloadAction<ImageLayoutAttributes>) => {
            let payload = action.payload;
            if (payload && payload.canvas_color) {
                payload = {
                    ...payload,
                    canvas_color: normalizeCanvasColorGradient(payload.canvas_color),
                };
            }
            if (state.imageProject?.layouts) {
                state.imageProject.layouts = state.imageProject.layouts.map((layout) =>
                    layout.id === action.payload.id ? action.payload : layout,
                );
            }
            state.imageLayout = payload;
            // state.hasChanged = true;
            safePushHistory(state, 'set-image-layout');
        },

        setSelectedImage: (
            state,
            action: PayloadAction<{ image: ImageAttributes | null; layoutId: string | null }>,
        ) => {
            state.selectedImage = action.payload;
        },

        setSelectedSlotIndex: (
            state,
            action: PayloadAction<{ slotIndex: number | null; layoutId: string | null }>,
        ) => {
            state.selectedSlotIndex = action.payload;
        },
        updateSelectedImageSetting: (
            state,
            action: PayloadAction<{ key: keyof ImageAttributes; value: any }>,
        ) => {
            if (!state.selectedImage?.image) return;
            const { key, value } = action.payload;
            let newValue = value;
            if (key === 'canvas_color') {
                newValue = normalizeCanvasColorGradient(value);
            }

            state.selectedImage.image = {
                ...state.selectedImage.image,
                [key]: newValue,
            };
            const selected = state.selectedImage.image;

            if (state.imageProject?.allImagesForLayouts && selected) {
                state.imageProject.allImagesForLayouts = state.imageProject.allImagesForLayouts.map(
                    (img) => (img.id === selected.id ? { ...img, ...selected } : img),
                );
            }
            state.hasChanged = true;
            console.log('hasChanged set to true in: updateSelectedImageSetting');
        },

        setImageProject: (state, action: PayloadAction<ImageProject | null>) => {
            state.imageProject = action.payload;
        },
        setTemporaryTexts: (state, action: PayloadAction<InlineText[]>) => {
            state.temperoryTexts = action.payload;
        },

        addText: (state, action: PayloadAction<InlineText>) => {
            state.temperoryTexts = [...state.temperoryTexts, action.payload];
            state.hasChanged = true;
            console.log('hasChanged set to true in: addText');
            safePushHistory(state, 'add-text');
        },
        updateText: (state, action: PayloadAction<InlineText>) => {
            const {
                id,
                content,
                color,
                size,
                font,
                rotation,
                text_shadow_offset_x,
                text_shadow_offset_y,
                text_shadow_blur,
                text_shadow_color,
                background_radius,
                background_width,
                background_height,
                stroke,
                stroke_color,
                background_color,
            } = action.payload;
            const text = state.temperoryTexts.find((t) => t.id === id);
            if (text) {
                text.content = content;
                text.color = color;
                text.size = size;
                text.font = font;
                text.rotation = rotation;
                text.text_shadow_offset_x = text_shadow_offset_x;
                text.text_shadow_offset_y = text_shadow_offset_y;
                text.text_shadow_blur = text_shadow_blur;
                text.text_shadow_color = text_shadow_color;
                text.stroke = stroke;
                text.stroke_color = stroke_color;
                text.background_radius = background_radius;
                text.background_width = background_width;
                text.background_height = background_height;
                text.background_color = background_color;
                state.selectedText = text;
                state.hasChanged = true;
                console.log('hasChanged set to true in: updateText');
            }
        },
        setSelectedText: (state, action: PayloadAction<InlineText | null>) => {
            state.selectedText = action.payload;
        },
        moveText: (state, { payload: { id, x, y } }) => {
            const t = state.temperoryTexts.find((t) => t.id === id);
            if (t) {
                t.location.x = x;
                t.location.y = y;
                state.hasChanged = true;
                console.log('hasChanged set to true in: moveText');
                safePushHistory(state, 'move-text', { groupId: `text:${id}:move`, replace: true });
            }
        },
        deleteText: (state, { payload: id }: PayloadAction<string>) => {
            state.temperoryTexts = state.temperoryTexts.filter((t) => t.id !== id);
            if (state.selectedText?.id === id) state.selectedText = null;
            state.hasChanged = true;
            console.log('hasChanged set to true in: deleteText');
            safePushHistory(state, 'delete-text');
        },

        setDraggedImage: (state, action: PayloadAction<ImageAttributes | null>) => {
            state.draggedImage = action.payload;
            state.hasChanged = true;
            console.log('hasChanged set to true in: setDraggedImage');
        },
        setHasChanged: (state, action: PayloadAction<boolean>) => {
            state.hasChanged = action.payload;

            console.log('hasChanged', state.hasChanged);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(updateImage.rejected, (state, action) => {
            console.error('Failed to update canvas color:', action.error);
        });
        builder.addCase(updateImage.pending, (state, action) => {});
        builder.addCase(updateImage.fulfilled, (state, action) => {
            // const { data } = action.payload;
            // const { id, attributes } = data;
            // if (state.imageProject?.allImagesForLayouts) {
            //     state.imageProject.allImagesForLayouts = state.imageProject.allImagesForLayouts.map(
            //         (img) => (img.id === id ? { ...img, ...attributes } : img),
            //     );
            // }
            // if (state.selectedImage?.image?.id === id) {
            //     state.selectedImage = {
            //         ...state.selectedImage,
            //         image: {
            //             ...state.selectedImage.image,
            //             ...attributes,
            //         },
            //     };
            // }
        });
        builder.addCase(uploadImage.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(uploadImage.fulfilled, (state, action: PayloadAction<ImageAttributes>) => {
            state.loading = false;
            state.uploadImages = [...state.uploadImages, action.payload];
            state.hasChanged = true;
            console.log('hasChanged set to true in: uploadImage.fulfilled');
        });
        builder.addCase(uploadImage.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(fetchUserUploads.fulfilled, (state, action) => {
            state.uploadImages = action.payload;
        });
        builder.addCase(downloadImage.pending, (state) => {
            state.downloadStatus.loading = true;
            state.downloadStatus.error = null;
        });
        builder.addCase(downloadImage.fulfilled, (state) => {
            state.downloadStatus.loading = false;
            state.downloadStatus.error = null;
        });
        builder.addCase(downloadImage.rejected, (state, action) => {
            state.downloadStatus.loading = false;
            state.downloadStatus.error = action.payload || 'Failed to download image';
        });
        builder.addCase(updateImageUrl.pending, (state) => {
            //state.loading = true;
            state.error = null;
        });
        builder.addCase(updateImageUrl.fulfilled, (state, action) => {
            const { data } = action.payload;
            const { id, attributes } = data;

            if (state.imageProject?.allImagesForLayouts) {
                state.imageProject.allImagesForLayouts = state.imageProject.allImagesForLayouts.map(
                    (img) => (img.id === id ? { ...img, ...attributes } : img),
                );
            }

            if (state.selectedImage?.image?.id === id) {
                state.selectedImage = {
                    ...state.selectedImage,
                    image: {
                        ...state.selectedImage.image,
                        ...attributes,
                    },
                };
            }

            state.loading = false;
        });
        builder.addCase(updateImageUrl.rejected, (state, action) => {
            // state.loading = false;
        });
        builder.addCase(enhanceImageUrl.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(enhanceImageUrl.fulfilled, (state, action) => {
            const { data } = action.payload;
            const { id, attributes } = data;
            if (state.imageProject?.allImagesForLayouts) {
                state.imageProject.allImagesForLayouts = state.imageProject.allImagesForLayouts.map(
                    (img) => (img.id === id ? { ...img, ...attributes } : img),
                );
            }

            if (state.selectedImage?.image?.id === id) {
                state.selectedImage = {
                    ...state.selectedImage,
                    image: {
                        ...state.selectedImage.image,
                        ...attributes,
                    },
                };
            }
            state.loading = false;
            state.hasChanged = true;
            console.log('hasChanged set to true in: enhanceImageUrl.fulfilled');
            safePushHistory(state, 'enhance-image');
        });
        builder.addCase(enhanceImageUrl.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(wizardImageUrl.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(wizardImageUrl.fulfilled, (state, action) => {
            const { data } = action.payload;
            const { id, attributes } = data;
            if (state.imageProject?.allImagesForLayouts) {
                state.imageProject.allImagesForLayouts = state.imageProject.allImagesForLayouts.map(
                    (img) => (img.id === id ? { ...img, ...attributes } : img),
                );
            }

            if (state.selectedImage?.image?.id === id) {
                state.selectedImage = {
                    ...state.selectedImage,
                    image: {
                        ...state.selectedImage.image,
                        ...attributes,
                    },
                };
            }
            state.loading = false;
            state.hasChanged = true;
            console.log('hasChanged set to true in: wizardImageUrl.fulfilled');
            safePushHistory(state, 'wizard-image');
        });
        builder.addCase(wizardImageUrl.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(removeBackgroundImageUrl.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(removeBackgroundImageUrl.fulfilled, (state, action) => {
            const { data } = action.payload;
            const { id, attributes } = data;
            if (state.imageProject?.allImagesForLayouts) {
                state.imageProject.allImagesForLayouts = state.imageProject.allImagesForLayouts.map(
                    (img) => (img.id === id ? { ...img, ...attributes } : img),
                );
            }

            if (state.selectedImage?.image?.id === id) {
                state.selectedImage = {
                    ...state.selectedImage,
                    image: {
                        ...state.selectedImage.image,
                        ...attributes,
                    },
                };
            }
            state.loading = false;
            state.hasChanged = true;
            console.log('hasChanged set to true in: removeBackgroundImageUrl.fulfilled');
            safePushHistory(state, 'remove-background');
        });
        builder.addCase(removeBackgroundImageUrl.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(resetImageUrl.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(resetImageUrl.fulfilled, (state, action) => {
            const { data } = action.payload;
            const { id, attributes } = data;
            if (state.imageProject?.allImagesForLayouts) {
                state.imageProject.allImagesForLayouts = state.imageProject.allImagesForLayouts.map(
                    (img) => (img.id === id ? { ...img, ...attributes } : img),
                );
            }

            if (state.selectedImage?.image?.id === id) {
                state.selectedImage = {
                    ...state.selectedImage,
                    image: {
                        ...state.selectedImage.image,
                        ...attributes,
                    },
                };
            }
            state.loading = false;
            state.hasChanged = true;
            console.log('hasChanged set to true in: resetImageUrl.fulfilled');
        });
        builder.addCase(resetImageUrl.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(fetchImageProject.fulfilled, (state, action) => {
            if (action.payload.layouts) {
                const layoutsWithSlotCount = action.payload.layouts.map((layout) => ({
                    ...layout,
                    slot_count: getSlotCountBySlug(layout.slug ?? '') ?? 1,
                }));

                state.imageProject = {
                    ...action.payload,
                    layouts: layoutsWithSlotCount,
                    allImagesForLayouts: action.payload.allImagesForLayouts,
                };

                state.imageLayout = layoutsWithSlotCount[0];
            } else {
                state.imageProject = action.payload;
            }
            safePushHistory(state, 'original');
        });
        builder.addCase(fetchImageProject.rejected, (state, action) => {
            state.imageProject = null;
        });
        builder.addCase(createImageLayout.fulfilled, (state, action) => {
            const layoutWithSlotCount = {
                id: action.payload?.id,
                slug: action.payload.slug ?? '',
                order: action.payload.order ?? 1,
                gap_size: action.payload.gap_size ?? 0,
                canvas_color: action.payload.canvas_color,
                slot_count: 1,
                transformation: action.payload.transformation ?? {
                    width: 100,
                    height: 100,
                    rotation: 0,
                    flip: { horizontal: false, vertical: false },
                },
                inline_text: action.payload.inline_text ?? [],
            };
            state.imageLayout = layoutWithSlotCount;

            if (state.imageProject) {
                state.imageProject.layouts = state.imageProject.layouts
                    ? [...state.imageProject.layouts, layoutWithSlotCount]
                    : [layoutWithSlotCount];
            }
            state.loading = false;
            state.selectedImage = { image: null, layoutId: null };
            // state.hasChanged = true;
            safePushHistory(state, 'create-image-layout');
        });
        builder.addCase(createImageLayout.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createImageLayout.rejected, (state, action) => {
            state.loading = false;
            state.imageLayout = null;
        });

        builder.addCase(updateImageLayout.fulfilled, (state, action) => {
            const slot_count = getSlotCountBySlug(state.imageLayout?.slug ?? '') ?? 1;

            state.imageLayout = action.payload;

            if (state.imageProject?.layouts) {
                state.imageProject.layouts = state.imageProject.layouts.map((layout) =>
                    layout.id === action.payload.id ? action.payload : layout,
                );
            }

            state.loading = false;
            //state.hasChanged = true;
            safePushHistory(state, 'update-image-project');
        });
        builder.addCase(updateImageLayout.pending, (state, action) => {
            // state.loading = true;
            state.error = null;
        });
        builder.addCase(updateImageLayout.rejected, (state, action) => {
            state.loading = false;
            state.imageLayout = null;
        });
        builder.addCase(updateImageProject.fulfilled, (state, action) => {
            state.imageProject = action.payload;
            state.loading = false;
            state.hasChanged = true;
            console.log('hasChanged set to true in: updateImageProject.fulfilled');
            safePushHistory(state, 'update-image-project');
        });
        builder.addCase(updateImageProject.rejected, (state, action) => {
            state.imageProject = null;
            state.loading = false;
        });
        builder.addCase(updateImageProject.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteImageLayout.fulfilled, (state, action) => {
            state.imageLayout = null;
            if (state.imageProject?.layouts) {
                state.imageProject.layouts = state.imageProject.layouts.filter(
                    (layout) => layout.id !== action.meta.arg.layoutId,
                );
                if (state.imageProject.layouts.length > 0) {
                    state.imageLayout = state.imageProject.layouts[0];
                }
            }
            state.loading = false;
            state.hasChanged = true;
            console.log('hasChanged set to true in: deleteImageLayout.fulfilled');
            safePushHistory(state, 'delete-layout');
        });
        builder.addCase(deleteImageLayout.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteImageLayout.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(addImagesToLayout.fulfilled, (state, action) => {
            if (state.imageProject && state.imageProject.layouts) {
                state.imageProject.layouts = state.imageProject.layouts.map((layout) =>
                    layout.id === action.payload.layoutId
                        ? {
                              ...layout,
                              images: action.payload.images.map((img) => ({
                                  id: img.id,
                                  meta: { order: img.meta.order },
                              })),
                          }
                        : layout,
                );

                const updatedLayout = state.imageProject.layouts.find(
                    (layout) => layout.id === action.payload.layoutId,
                );
                if (updatedLayout) {
                    state.imageLayout = updatedLayout;
                    state.selectedImage = { image: null, layoutId: action.payload.layoutId };
                }
                console.log(state.selectedImage, 'state.selectedImage');
                state.loading = false;
                state.hasChanged = true;
                console.log('hasChanged set to true in: addImagesToLayout.fulfilled');
                safePushHistory(state, 'add-image');
            }
        });
        builder.addCase(addImagesToLayout.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addImagesToLayout.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Failed to add images to layout';
        });
        builder.addCase(changeImageOrder.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(changeImageOrder.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(changeImageOrder.fulfilled, (state, action) => {
            if (state.imageProject && state.imageProject.layouts) {
                state.imageProject.layouts = state.imageProject.layouts.map((layout) =>
                    layout.id === action.payload.layoutId
                        ? {
                              ...layout,
                              images: action.payload.images.map((img) => ({
                                  id: img.id,
                                  meta: { order: img.meta.order },
                              })),
                          }
                        : layout,
                );

                const updatedLayout = state.imageProject.layouts.find(
                    (layout) => layout.id === action.payload.layoutId,
                );
                if (updatedLayout) {
                    state.imageLayout = updatedLayout;
                    state.selectedImage = { image: null, layoutId: action.payload.layoutId };
                }
                state.loading = false;
                state.hasChanged = true;
                console.log('hasChanged set to true in: changeImageOrder.fulfilled');
                safePushHistory(state, 'order');
            }
        });
        builder.addCase(deleteImageFromLayout.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteImageFromLayout.rejected, (state, action) => {
            state.loading = false;
        });
        builder
            .addCase(deleteImageFromLayout.fulfilled, (state, action) => {
                state.loading = false;
                const imageId = action.payload.imageId;
                // delete imageProject.allImagesForLayouts
                if (state.imageProject) {
                    state.imageProject.allImagesForLayouts =
                        state.imageProject.allImagesForLayouts.filter((img) => img.id !== imageId);
                    // delete from all layouts.images
                    state.imageProject.layouts = state.imageProject.layouts.map((layout) => ({
                        ...layout,
                        images: layout.images?.filter((img) => img.id !== imageId),
                    }));
                }
                // delete from imageLayout.images, if selected layout
                if (state.imageLayout && state.imageLayout.images) {
                    state.imageLayout.images = state.imageLayout.images.filter(
                        (img) => img.id !== imageId,
                    );
                }
                state.hasChanged = true;
                console.log('hasChanged set to true in: deleteImageFromLayout.fulfilled');
                safePushHistory(state, 'delete');
            })

            .addCase(generateImage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(generateImage.fulfilled, (state, action: PayloadAction<ImageAttributes>) => {
                state.loading = false;
                if (state.imageProject) {
                    state.imageProject.allImagesForLayouts = [
                        ...state.imageProject.allImagesForLayouts,
                        action.payload,
                    ];
                }
                state.hasChanged = true;
                console.log('hasChanged set to true in: generateImage.fulfilled');
                safePushHistory(state, 'generate');
            })
            .addCase(generateImage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to generate image';
            })
            .addCase(updatePosition.fulfilled, (state, action) => {
                state.hasChanged = true;
                console.log('hasChanged set to true in: updatePosition.fulfilled');
                const imgId = state.selectedImage?.image?.id ?? 'none';
                safePushHistory(state, 'move', {
                    groupId: `img:${imgId}:transform`,
                    replace: true,
                });

                // if (state.imageProject) {
                //     state.imageProject.allImagesForLayouts =
                //         state.imageProject.allImagesForLayouts.map((img) =>
                //             img.id === state.selectedImage.image?.id
                //                 ? {
                //                       ...img,
                //                       transformation: {
                //                           ...img.transformation,
                //                           ...action.payload,
                //                       },
                //                   }
                //                 : img,
                //         );
                //     if (state.selectedImage.image) {
                //         state.selectedImage = {
                //             image: {
                //                 ...state.selectedImage.image,
                //                 transformation: {
                //                     ...state.selectedImage.image.transformation,
                //                     ...action.payload,
                //                 },
                //             },
                //             layoutId: state.selectedImage.layoutId,
                //         };
                //     }
                // }
            })
            .addCase(updateTransformation.fulfilled, (state, action) => {
                state.hasChanged = true;
                console.log('hasChanged set to true in: updateTransformation.fulfilled');
                const imgId = state.selectedImage?.image?.id ?? 'none';
                safePushHistory(state, 'transform', {
                    groupId: `img:${imgId}:transform`,
                    replace: true,
                });
            })
            .addCase(updateZoom.fulfilled, (state, action) => {
                state.hasChanged = true;
                console.log('hasChanged set to true in: updateZoom.fulfilled');
                const imgId = state.selectedImage?.image?.id ?? 'none';
                safePushHistory(state, 'zoom', {
                    groupId: `img:${imgId}:transform`,
                    replace: true,
                });
                // if (state.imageProject) {
                //     state.imageProject.allImagesForLayouts =
                //         state.imageProject.allImagesForLayouts.map((img) =>
                //             img.id === state.selectedImage.image?.id
                //                 ? {
                //                       ...img,
                //                       transformation: {
                //                           ...img.transformation,
                //                           ...action.payload,
                //                       },
                //                   }
                //                 : img,
                //         );
                //     if (state.selectedImage.image) {
                //         state.selectedImage = {
                //             image: {
                //                 ...state.selectedImage.image,
                //                 transformation: {
                //                     ...state.selectedImage.image.transformation,
                //                     ...action.payload,
                //                 },
                //             },
                //             layoutId: state.selectedImage.layoutId,
                //         };
                //     }
                // }
            });
    },
});

export function safePushHistory(
    state: ImageState,
    action: string,
    opts?: { groupId?: string; replace?: boolean },
) {
    const snapshot = makeSnapshot(state);

    // Prevent exact duplicates (same action & same snapshot as last history item)
    const last = state.history[state.history.length - 1];
    const isDuplicate =
        last && last.action === action && JSON.stringify(last.value) === JSON.stringify(snapshot);

    if (isDuplicate) {
        state.future = [];
        return;
    }

    const entry: ImageHistoryEntry & { groupId?: string } = {
        type: 'imageProject',
        action,
        value: snapshot,
        ...(opts?.groupId ? { groupId: opts.groupId } : {}),
    };

    // Group compression (e.g., move/zoom): replace last entry if same groupId
    if (opts?.replace && opts.groupId && last?.groupId === opts.groupId) {
        state.history[state.history.length - 1] = entry;
        state.future = [];
        return;
    }

    cappedUnshift(state.history, entry, HISTORY_LIMIT);
    state.future = [];
}

type AppThunk<ReturnType = void> = (dispatch: AppDispatch, getState: () => RootState) => ReturnType;

const ENHANCE_ACTIONS = new Set(['enhance-image', 'wizard-image', 'remove-background']);

export const undo = (): AppThunk => async (dispatch, getState) => {
    const { image } = getState();
    const idx = image.history.length - 1;

    // Correct boundary: allow undo when there's at least 1 prior snapshot (idx >= 1)
    if (idx <= 0) return;

    const current = image.history[idx];
    const isEnhanceStep = ENHANCE_ACTIONS.has(current.action);
    const imgId = current.value?.selectedImage?.image?.id;

    try {
        // If the last step was an enhancement that changed the server URL, revert it first.
        if (isEnhanceStep && imgId) {
            await dispatch(resetImageUrl({ imageId: imgId }));
        }
    } finally {
        // ALWAYS apply the pure undo state transition
        dispatch(undoHistory());
    }
};

export const {
    // setTemporaryLayout,
    setSelectedImage,
    updateSelectedImageSetting,
    setSelectedSlotIndex,
    setImageProject,
    addText,
    updateText,
    setSelectedText,
    moveText,
    deleteText,
    setTemporaryTexts,
    setLoadingSave,
    setLoadingCancel,
    //  clearTemporaryLayout,
    setAllImagesForLayouts,
    setImageLayout,
    setDraggedImage,
    setLoadingDownload,
    setHasChanged,
    commitHistory,
    logHistory,
    undoHistory,
    clearHistory,
} = imageSlice.actions;

export default imageSlice.reducer;
